import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Search, Save, CheckCircle2, AlertCircle, X, Minus, Award, ChevronDown, Shield } from 'lucide-react';
import { sanitizeMosTitle } from '@/utils/militaryTranslation';
import * as help from '@/lib/helpStore';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BRANCHES = ['Army', 'Navy', 'Air Force', 'Marine Corps', 'Coast Guard', 'Space Force'];

// Canonical rank map by branch
const RANKS_BY_BRANCH = {
  'Army': [
    // Enlisted
    'E-1 (PVT) Private',
    'E-2 (PV2) Private Second Class',
    'E-3 (PFC) Private First Class',
    'E-4 (SPC) Specialist',
    'E-4 (CPL) Corporal',
    'E-5 (SGT) Sergeant',
    'E-6 (SSG) Staff Sergeant',
    'E-7 (SFC) Sergeant First Class',
    'E-8 (MSG) Master Sergeant',
    'E-8 (1SG) First Sergeant',
    'E-9 (SGM) Sergeant Major',
    'E-9 (CSM) Command Sergeant Major',
    'E-9 (SMA) Sergeant Major of the Army',
    // Warrant Officers
    'W-1 (WO1) Warrant Officer 1',
    'W-2 (CW2) Chief Warrant Officer 2',
    'W-3 (CW3) Chief Warrant Officer 3',
    'W-4 (CW4) Chief Warrant Officer 4',
    'W-5 (CW5) Chief Warrant Officer 5',
    // Officers
    'O-1 (2LT) Second Lieutenant',
    'O-2 (1LT) First Lieutenant',
    'O-3 (CPT) Captain',
    'O-4 (MAJ) Major',
    'O-5 (LTC) Lieutenant Colonel',
    'O-6 (COL) Colonel',
    'O-7 (BG) Brigadier General',
    'O-8 (MG) Major General',
    'O-9 (LTG) Lieutenant General',
    'O-10 (GEN) General',
  ],
  'Navy': [
    // Enlisted
    'E-1 (SR) Seaman Recruit',
    'E-2 (SA) Seaman Apprentice',
    'E-3 (SN) Seaman',
    'E-4 (PO3) Petty Officer Third Class',
    'E-5 (PO2) Petty Officer Second Class',
    'E-6 (PO1) Petty Officer First Class',
    'E-7 (CPO) Chief Petty Officer',
    'E-8 (SCPO) Senior Chief Petty Officer',
    'E-9 (MCPO) Master Chief Petty Officer',
    'E-9 (MCPON) Master Chief Petty Officer of the Navy',
    // Warrant Officers
    'W-2 (CWO2) Chief Warrant Officer 2',
    'W-3 (CWO3) Chief Warrant Officer 3',
    'W-4 (CWO4) Chief Warrant Officer 4',
    'W-5 (CWO5) Chief Warrant Officer 5',
    // Officers
    'O-1 (ENS) Ensign',
    'O-2 (LTJG) Lieutenant Junior Grade',
    'O-3 (LT) Lieutenant',
    'O-4 (LCDR) Lieutenant Commander',
    'O-5 (CDR) Commander',
    'O-6 (CAPT) Captain',
    'O-7 (RDML) Rear Admiral Lower Half',
    'O-8 (RADM) Rear Admiral Upper Half',
    'O-9 (VADM) Vice Admiral',
    'O-10 (ADM) Admiral',
  ],
  'Air Force': [
    // Enlisted
    'E-1 (AB) Airman Basic',
    'E-2 (Amn) Airman',
    'E-3 (A1C) Airman First Class',
    'E-4 (SrA) Senior Airman',
    'E-5 (SSgt) Staff Sergeant',
    'E-6 (TSgt) Technical Sergeant',
    'E-7 (MSgt) Master Sergeant',
    'E-8 (SMSgt) Senior Master Sergeant',
    'E-9 (CMSgt) Chief Master Sergeant',
    'E-9 (CMSAF) Chief Master Sergeant of the Air Force',
    // Officers
    'O-1 (2d Lt) Second Lieutenant',
    'O-2 (1st Lt) First Lieutenant',
    'O-3 (Capt) Captain',
    'O-4 (Maj) Major',
    'O-5 (Lt Col) Lieutenant Colonel',
    'O-6 (Col) Colonel',
    'O-7 (Brig Gen) Brigadier General',
    'O-8 (Maj Gen) Major General',
    'O-9 (Lt Gen) Lieutenant General',
    'O-10 (Gen) General',
  ],
  'Marine Corps': [
    // Enlisted
    'E-1 (Pvt) Private',
    'E-2 (PFC) Private First Class',
    'E-3 (LCpl) Lance Corporal',
    'E-4 (Cpl) Corporal',
    'E-5 (Sgt) Sergeant',
    'E-6 (SSgt) Staff Sergeant',
    'E-7 (GySgt) Gunnery Sergeant',
    'E-8 (MSgt) Master Sergeant',
    'E-8 (1stSgt) First Sergeant',
    'E-9 (MGySgt) Master Gunnery Sergeant',
    'E-9 (SgtMaj) Sergeant Major',
    'E-9 (SgtMajMC) Sergeant Major of the Marine Corps',
    // Warrant Officers
    'W-1 (WO) Warrant Officer',
    'W-2 (CWO2) Chief Warrant Officer 2',
    'W-3 (CWO3) Chief Warrant Officer 3',
    'W-4 (CWO4) Chief Warrant Officer 4',
    'W-5 (CWO5) Chief Warrant Officer 5',
    // Officers
    'O-1 (2ndLt) Second Lieutenant',
    'O-2 (1stLt) First Lieutenant',
    'O-3 (Capt) Captain',
    'O-4 (Maj) Major',
    'O-5 (LtCol) Lieutenant Colonel',
    'O-6 (Col) Colonel',
    'O-7 (BGen) Brigadier General',
    'O-8 (MajGen) Major General',
    'O-9 (LtGen) Lieutenant General',
    'O-10 (Gen) General',
  ],
  'Coast Guard': [
    // Enlisted
    'E-1 (SR) Seaman Recruit',
    'E-2 (SA) Seaman Apprentice',
    'E-3 (SN) Seaman',
    'E-4 (PO3) Petty Officer Third Class',
    'E-5 (PO2) Petty Officer Second Class',
    'E-6 (PO1) Petty Officer First Class',
    'E-7 (CPO) Chief Petty Officer',
    'E-8 (SCPO) Senior Chief Petty Officer',
    'E-9 (MCPO) Master Chief Petty Officer',
    'E-9 (MCPOCG) Master Chief Petty Officer of the Coast Guard',
    // Warrant Officers
    'W-2 (CWO2) Chief Warrant Officer 2',
    'W-3 (CWO3) Chief Warrant Officer 3',
    'W-4 (CWO4) Chief Warrant Officer 4',
    // Officers
    'O-1 (ENS) Ensign',
    'O-2 (LTJG) Lieutenant Junior Grade',
    'O-3 (LT) Lieutenant',
    'O-4 (LCDR) Lieutenant Commander',
    'O-5 (CDR) Commander',
    'O-6 (CAPT) Captain',
    'O-7 (RDML) Rear Admiral Lower Half',
    'O-8 (RADM) Rear Admiral Upper Half',
    'O-9 (VADM) Vice Admiral',
    'O-10 (ADM) Admiral',
  ],
  'Space Force': [
    // Enlisted
    'E-1 (Spc1) Specialist 1',
    'E-2 (Spc2) Specialist 2',
    'E-3 (Spc3) Specialist 3',
    'E-4 (Spc4) Specialist 4',
    'E-5 (Sgt) Sergeant',
    'E-6 (TSgt) Technical Sergeant',
    'E-7 (MSgt) Master Sergeant',
    'E-8 (SMSgt) Senior Master Sergeant',
    'E-9 (CMSgt) Chief Master Sergeant',
    'E-9 (CMSSF) Chief Master Sergeant of the Space Force',
    // Officers
    'O-1 (2d Lt) Second Lieutenant',
    'O-2 (1st Lt) First Lieutenant',
    'O-3 (Capt) Captain',
    'O-4 (Maj) Major',
    'O-5 (Lt Col) Lieutenant Colonel',
    'O-6 (Col) Colonel',
    'O-7 (Brig Gen) Brigadier General',
    'O-8 (Maj Gen) Major General',
    'O-9 (Lt Gen) Lieutenant General',
    'O-10 (Gen) General',
  ],
};

const _normBranch = (s) => s.toLowerCase().replace(/\./g, '').replace(/u\s*s\s*/g, '').trim();

/**
 * Extract the BRANCH designator from an MOS/AOC title. Titles can carry multiple
 * parentheticals — a descriptive qualifier and the canonical "(Branch - Category)"
 * designator, e.g. "Rotary Wing Aviator (Aircraft Nonspecific) (Army - Warrant Officer only)".
 * We must return "Army" here, not the first "(Aircraft Nonspecific)". Strategy: scan all
 * parentheticals, prefer the one whose leading segment names a known branch; otherwise fall
 * back to the last parenthetical that carries a "X - Y" classification.
 */
function _extractBranchFromTitle(title) {
  if (!title) return null;
  const parens = [...title.matchAll(/\(([^)]+)\)/g)].map(m => m[1]);
  if (parens.length === 0) return null;
  for (const inner of parens) {
    const lead = inner.split('-')[0].trim();
    if (BRANCHES.some(b => _normBranch(b) === _normBranch(lead))) return lead;
  }
  for (let i = parens.length - 1; i >= 0; i--) {
    if (parens[i].includes('-')) return parens[i].split('-')[0].trim();
  }
  return null;
}

/**
 * Check if user-selected branch matches the MOS title's branch.
 */
function _branchesMatch(selectedBranch, mosBranch) {
  const normalize = (s) => s.toLowerCase().replace(/\./g, '').replace(/u\s*s\s*/g, '').trim();
  const sel = normalize(selectedBranch);
  const mos = normalize(mosBranch);
  if (sel === mos) return true;
  // Handle common aliases
  if (sel.includes('marine') && mos.includes('marine')) return true;
  if (sel.includes('air force') && mos.includes('air force')) return true;
  if (sel.includes('coast guard') && mos.includes('coast guard')) return true;
  if (sel.includes('army') && mos.includes('army')) return true;
  if (sel.includes('navy') && mos.includes('navy')) return true;
  return false;
}

const _makeEmptyRole = () => ({
  branch: '',
  rank: '',
  start_month: 1,
  start_year: 2020,
  end_month: 1,
  end_year: 2024,
  location: '',
  mos_code: '',
  mos_title: '',
  mos_data: null,
  selected_knowledge: [],
  selected_skills: [],
  selected_abilities: [],
  resume_bullets: [],
  base_snapshot: null,
  security_clearance: null,
  security_clearance_status: null,
  raw_duty_evidence: '',
  raw_accomplishment_evidence: ''
});

function MilitaryStep({ resume, saveResume, onNext, saving, registerPendingData, registerStepValidator, onMilitaryChanged }) {
  const [roles, setRoles] = useState([]);
  const [currentRole, setCurrentRole] = useState(null);
  const [mosDialogOpen, setMosDialogOpen] = useState(false);
  const [mosData, setMosData] = useState(null);
  const [loadingMos, setLoadingMos] = useState(false);
  const [selectedKSA, setSelectedKSA] = useState({ knowledge: [], skills: [], abilities: [] });
  // Single open KSA description across the whole modal (display-only; never affects selection/scoring).
  const [expandedKSA, setExpandedKSA] = useState(null);
  // One-time contextual tip (Help & Tips): shown below the unchanged modal instructions.
  const [showKsaTip, setShowKsaTip] = useState(false);
  useEffect(() => {
    if (mosDialogOpen) {
      const role = help.getHelpState().role;
      setShowKsaTip(help.autoTipsOn() && !help.ksaTipDone() && role !== 'admin');
    } else {
      setShowKsaTip(false);
    }
  }, [mosDialogOpen]);
  const toggleExpand = (key) => {
    setExpandedKSA((prev) => (prev === key ? null : key));
    if (showKsaTip) { help.completeKsaTip(); setShowKsaTip(false); }
  };
  const [mosLookupError, setMosLookupError] = useState(null);
  const [branchClearedMsg, setBranchClearedMsg] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  // Career-level fields (shared with Step 5 / rendered career-wide)
  const [structuredAwards, setStructuredAwards] = useState([]);
  const [careerClearance, setCareerClearance] = useState('');   // base level string
  const [careerClearanceSci, setCareerClearanceSci] = useState(false);
  const [careerClearanceAccess, setCareerClearanceAccess] = useState('');  // current_access | previously_held | unknown
  // Military Qualifications & Awards pickers (both share structured_awards)
  const [qualOpen, setQualOpen] = useState(false);
  const [qualSearch, setQualSearch] = useState('');
  const [qualResults, setQualResults] = useState([]);
  const [qualCatalog, setQualCatalog] = useState({});
  const [awardsOpen, setAwardsOpen] = useState(false);
  const [awardSearch, setAwardSearch] = useState('');
  const [awardResults, setAwardResults] = useState([]);

  // Load roles from resume. For a brand-new resume with zero positions, present ONE ready-to-edit
  // draft position immediately (local-only; not persisted until the user enters data + saves),
  // so the first military position never requires an extra "Add" click.
  useEffect(() => {
    if (!resume) return;
    if (resume.military_roles && resume.military_roles.length > 0) {
      console.log('[MilitaryStep] Loading from resume');
      setRoles(resume.military_roles);
    } else {
      setRoles([_makeEmptyRole()]);
    }
    setIsDirty(false);
  }, [resume?.military_roles]);

  // Hydrate career-level clearance + shared awards (display normalization only —
  // never infers clearance from legacy per-position data per product decision).
  useEffect(() => {
    if (!resume) return;
    setStructuredAwards(resume.structured_awards || []);
    let lvl = resume.security_clearance || '';
    let sci = !!resume.security_clearance_sci;
    if (lvl === 'ts_sci') { lvl = 'top_secret'; sci = true; }
    else if (lvl === 'q_clearance') { lvl = 'doe_q'; }
    else if (lvl === 'l_clearance') { lvl = 'doe_l'; }
    setCareerClearance(lvl);
    setCareerClearanceSci(sci);
    setCareerClearanceAccess(resume.security_clearance_access_status || '');
  }, [resume?.structured_awards, resume?.security_clearance, resume?.security_clearance_sci]);

  // Full catalog (all categories) for qualification display names
  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/resume/award-catalog`, { params: { q: '', limit: 500 }, withCredentials: true });
        const map = {};
        for (const a of resp.data.awards || []) map[a.award_id] = a;
        setQualCatalog(map);
      } catch { /* graceful */ }
    })();
  }, []);

  // Debounced qualification browse/search — qualification categories ONLY
  // (badges/tabs/schools/warfare/specialty). Decorations never appear here.
  useEffect(() => {
    if (!qualOpen) return;
    const t = setTimeout(async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/resume/award-catalog`, { params: { q: qualSearch, limit: 25, group: 'qualification' }, withCredentials: true });
        setQualResults(resp.data.awards || []);
      } catch { setQualResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [qualSearch, qualOpen]);

  // Debounced military-awards browse/search — decoration categories ONLY.
  // Same shared structured_awards records / catalog as Step 5.
  useEffect(() => {
    if (!awardsOpen) return;
    const t = setTimeout(async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/resume/award-catalog`, { params: { q: awardSearch, limit: 25, group: 'decoration' }, withCredentials: true });
        setAwardResults(resp.data.awards || []);
      } catch { setAwardResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [awardSearch, awardsOpen]);

  const buildCareerPayload = () => ({
    military_roles: roles,
    structured_awards: structuredAwards,
    security_clearance: careerClearance || '',
    security_clearance_sci: careerClearanceSci,
    security_clearance_access_status: careerClearance ? (careerClearanceAccess || '') : '',
  });

  // Register pending data whenever any tracked field changes (save-on-navigation)
  useEffect(() => {
    if (isDirty && registerPendingData) {
      registerPendingData(buildCareerPayload());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, structuredAwards, careerClearance, careerClearanceSci, careerClearanceAccess, isDirty, registerPendingData]);

  const clearanceUiValue = (careerClearance === 'top_secret' && careerClearanceSci) ? 'top_secret_sci' : careerClearance;

  const onClearanceChange = (uiVal) => {
    if (!uiVal) { setCareerClearance(''); setCareerClearanceSci(false); setCareerClearanceAccess(''); }
    else if (uiVal === 'top_secret_sci') { setCareerClearance('top_secret'); setCareerClearanceSci(true); }
    else { setCareerClearance(uiVal); setCareerClearanceSci(false); }
    setIsDirty(true);
  };

  const addQualification = (entry) => {
    if (structuredAwards.find(a => a.award_id === entry.award_id)) {
      toast.info(`${entry.display_name} already added`);
      return;
    }
    setStructuredAwards(prev => [...prev, { award_id: entry.award_id, display_name: entry.display_name, quantity: 1, category: entry.category }]);
    setIsDirty(true);
    setQualSearch('');
    setAwardSearch('');
    // Close the picker immediately after a successful selection so the long
    // results list disappears and the user returns to the collapsed state.
    // The selected chip stays visible; reopening requires an intentional click.
    setQualOpen(false);
    setAwardsOpen(false);
  };

  const removeQualification = (awardId) => {
    setStructuredAwards(prev => prev.filter(a => a.award_id !== awardId));
    setIsDirty(true);
  };

  const updateQualQty = (awardId, delta) => {
    setStructuredAwards(prev => prev.map(a => {
      if (a.award_id !== awardId) return a;
      return { ...a, quantity: Math.max(1, Math.min(20, (a.quantity || 1) + delta)) };
    }));
    setIsDirty(true);
  };

  // Category split (item.category, else resolved from catalog). Qualifications vs
  // decorations are two views of the SAME structured_awards array.
  const QUAL_CATS = new Set(['badge', 'tab', 'qualification', 'warfare_qualification', 'military_school', 'specialty_qualification']);
  const catOf = (sa) => sa.category || qualCatalog[sa.award_id]?.category;
  const qualificationItems = structuredAwards.filter(sa => QUAL_CATS.has(catOf(sa)));
  const awardItems = structuredAwards.filter(sa => !QUAL_CATS.has(catOf(sa)));

  // Gate forward navigation: require at least one role with valid MOS/KSA data
  useEffect(() => {
    if (!registerStepValidator) return;
    registerStepValidator(() => {
      const hasValidMOS = roles.some(role =>
        role.mos_data &&
        (role.selected_knowledge?.length > 0 || role.selected_skills?.length > 0 || role.selected_abilities?.length > 0)
      );
      if (!hasValidMOS) {
        toast.error('Please look up and save specialty details before continuing.');
        return false;
      }
      return true;
    });
  }, [roles, registerStepValidator]);

  const addRole = () => {
    setRoles([...roles, _makeEmptyRole()]);
    setIsDirty(true);
  };

  const removeRole = (index) => {
    setRoles(roles.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const updateRole = useCallback((index, field, value) => {
    setRoles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // If location changes, invalidate stale base_snapshot immediately
      if (field === 'location') {
        updated[index].base_snapshot = null;
      }
      
      // If branch changes, reset rank if current rank is not valid for new branch
      if (field === 'branch') {
        const validRanks = RANKS_BY_BRANCH[value] || [];
        if (!validRanks.includes(updated[index].rank)) {
          updated[index].rank = '';
        }
        // Clear incompatible MOS data if loaded MOS doesn't match new branch
        const mosTitle = updated[index].mos_title || '';
        if (mosTitle && value) {
          const titleBranch = _extractBranchFromTitle(mosTitle);
          if (titleBranch && !_branchesMatch(value, titleBranch)) {
            updated[index].mos_code = '';
            updated[index].mos_title = '';
            updated[index].mos_data = null;
            updated[index].selected_knowledge = [];
            updated[index].selected_skills = [];
            updated[index].selected_abilities = [];
            updated[index].resume_bullets = [];
            updated[index].ai_summary_text = null;
            updated[index].ai_summary_hash = null;
            setBranchClearedMsg(`MOS selection cleared — it did not match ${value}.`);
            setTimeout(() => setBranchClearedMsg(null), 5000);
          }
        }
      }
      
      return updated;
    });
    setIsDirty(true);
  }, []);

  const lookupMOS = async (roleIndex) => {
    const role = roles[roleIndex];
    if (!role.mos_code) {
      toast.error('Please enter a specialty code');
      return;
    }
    if (!role.branch) {
      setMosLookupError('Please select a branch before looking up your specialty code.');
      return;
    }

    setLoadingMos(true);
    setCurrentRole(roleIndex);
    setMosLookupError(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/resume/mos-lookup`,
        { mos_code: role.mos_code.trim().toUpperCase() },
        { withCredentials: true }
      );

      // Validate response has KSA data
      if (!response.data.knowledge?.length && !response.data.skills?.length && !response.data.abilities?.length) {
        setMosLookupError('No Knowledge, Skills, or Abilities data found for this specialty code.');
        return;
      }

      // Validate branch compatibility using the title's branch hint
      const mosTitle = response.data.title || '';
      const mosBranch = _extractBranchFromTitle(mosTitle);
      if (mosBranch && !_branchesMatch(role.branch, mosBranch)) {
        setMosLookupError(`Code ${role.mos_code} belongs to ${mosBranch}, but you selected ${role.branch}. Please correct the branch or specialty code.`);
        return;
      }

      setMosData(response.data);
      
      // Update role with MOS title
      const updated = [...roles];
      updated[roleIndex].mos_title = response.data.title || '';
      setRoles(updated);
      
      // Detect if MOS actually changed from what was previously saved
      const savedMosCode = resume?.military_roles?.[roleIndex]?.mos_code || '';
      const newMosCode = role.mos_code.trim().toUpperCase();
      const mosChanged = savedMosCode && savedMosCode !== newMosCode;
      
      // Pre-select: if MOS changed, force new auto-selection.
      // If MOS is same and user already has selections, preserve those.
      const existingK = role.selected_knowledge || [];
      const existingS = role.selected_skills || [];
      const existingA = role.selected_abilities || [];
      const hasExisting = existingK.length + existingS.length + existingA.length > 0;
      
      if (hasExisting && !mosChanged) {
        setSelectedKSA({ knowledge: existingK, skills: existingS, abilities: existingA });
      } else {
        // Auto-select top ~12 items by importance across all categories
        const ranked = response.data.ranked_items || [];
        const autoK = [], autoS = [], autoA = [];
        let count = 0;
        for (const item of ranked) {
          if (count >= 12) break;
          if (item.importance < 50) break; // skip low-signal items
          if (item.category === 'knowledge' && !autoK.includes(item.name)) { autoK.push(item.name); count++; }
          else if (item.category === 'skill' && !autoS.includes(item.name)) { autoS.push(item.name); count++; }
          else if (item.category === 'ability' && !autoA.includes(item.name)) { autoA.push(item.name); count++; }
        }
        setSelectedKSA({ knowledge: autoK, skills: autoS, abilities: autoA });
      }
      
      setMosDialogOpen(true);
      toast.success(`MOS found: ${response.data.title || response.data.mos_code}`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Specialty code not found. Please check the code and selected branch.';
      setMosLookupError(errorMsg);
    } finally {
      setLoadingMos(false);
    }
  };

  const toggleKSA = (type, item) => {
    setSelectedKSA(prev => {
      const list = prev[type];
      const updated = list.includes(item)
        ? list.filter(i => i !== item)
        : [...list, item];
      return { ...prev, [type]: updated };
    });
  };

  const saveKSASelection = async () => {
    // Validate at least some KSA selected
    const totalSelected = selectedKSA.knowledge.length + selectedKSA.skills.length + selectedKSA.abilities.length;
    if (totalSelected === 0) {
      toast.error('Please select at least one Knowledge, Skill, or Ability');
      return;
    }

    try {
      // First generate bullets from selected KSAs
      const bulletResponse = await axios.post(
        `${BACKEND_URL}/api/resume/generate-bullets`,
        selectedKSA,
        { withCredentials: true }
      );

      // Build updated roles array with the new KSA selections
      const updatedRoles = [...roles];
      updatedRoles[currentRole] = {
        ...updatedRoles[currentRole],
        mos_data: mosData,
        mos_title: mosData?.title || '',
        selected_knowledge: selectedKSA.knowledge,
        selected_skills: selectedKSA.skills,
        selected_abilities: selectedKSA.abilities,
        resume_bullets: bulletResponse.data.bullets
      };
      
      // CRITICAL: Actually save to database (not just local state)
      console.log(`[KSA SAVE] Saving ${totalSelected} KSA items for role ${currentRole}`);
      const savedResume = await saveResume({
        military_roles: updatedRoles,
        structured_awards: structuredAwards,
        security_clearance: careerClearance || '',
        security_clearance_sci: careerClearanceSci,
        security_clearance_access_status: careerClearance ? (careerClearanceAccess || '') : '',
      });
      
      // Update local state from backend response (single source of truth)
      if (savedResume?.military_roles) {
        setRoles(savedResume.military_roles);
        setIsDirty(false); // Reset dirty flag after successful save
        const savedRole = savedResume.military_roles[currentRole];
        const savedCount = (savedRole?.selected_knowledge?.length || 0) + 
                          (savedRole?.selected_skills?.length || 0) + 
                          (savedRole?.selected_abilities?.length || 0);
        console.log(`[KSA SAVE] Backend confirmed: ${savedCount} KSA items saved for role ${currentRole}`);
      }
      if (savedResume) {
        setStructuredAwards(savedResume.structured_awards || []);
      }
      
      setMosDialogOpen(false);
      toast.success(`${totalSelected} KSA items saved`);
      // Notify parent that military data changed materially
      if (onMilitaryChanged) onMilitaryChanged();
    } catch (error) {
      console.error('[KSA SAVE] Failed:', error);
      toast.error('Failed to save KSA selections');
    }
  };

  const getRanksForBranch = (branch) => {
    return RANKS_BY_BRANCH[branch] || [];
  };

  return (
    <div className="space-y-6">
      <p className="text-slate-600">Add your military service history. At least one role with valid MOS data is required.</p>

      {roles.map((role, index) => (
        <Card key={index} className="p-6 border border-slate-200" data-testid={`military-role-${index}`}>
          <div className="flex justify-between items-start mb-4">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-slate-900">Military Role {index + 1}</h3>
              {(role.rank || role.mos_title || role.mos_code) && (
                <p className="text-xs text-slate-500 mt-0.5 truncate" data-testid={`military-role-summary-${index}`}>
                  {[role.rank, role.mos_title ? `${role.mos_code ? role.mos_code + ' ' : ''}${sanitizeMosTitle(role.mos_title, role.rank)}` : role.mos_code].filter(Boolean).join(' | ')}
                </p>
              )}
            </div>
            <Button
              data-testid={`remove-role-${index}-btn`}
              onClick={() => removeRole(index)}
              variant="ghost"
              size="sm"
              className="text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-900 font-medium">Branch *</Label>
                <select
                  data-testid={`branch-select-${index}`}
                  value={role.branch}
                  onChange={(e) => updateRole(index, 'branch', e.target.value)}
                  className="flex h-12 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm mt-2 focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="">Select Branch</option>
                  {BRANCHES.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-slate-900 font-medium">Rank *</Label>
                <select
                  data-testid={`rank-select-${index}`}
                  value={role.rank}
                  onChange={(e) => updateRole(index, 'rank', e.target.value)}
                  disabled={!role.branch}
                  className={`flex h-12 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm mt-2 focus:outline-none focus:ring-2 focus:ring-brand ${!role.branch ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">{role.branch ? 'Select Rank' : 'Select Branch first'}</option>
                  {getRanksForBranch(role.branch).map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {!role.branch && (
                  <p className="text-xs text-slate-500 mt-1">Select a branch to enable rank selection</p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-slate-900 font-medium">Duty Station / Location *</Label>
              <Input
                data-testid={`location-input-${index}`}
                value={role.location}
                onChange={(e) => updateRole(index, 'location', e.target.value)}
                className="h-12 mt-2"
                placeholder="Fort Bragg, NC"
              />
              {role.base_snapshot && (
                <p className="text-xs text-brand mt-1 flex items-center gap-1" data-testid={`base-match-${index}`}>
                  <CheckCircle2 className="w-3 h-3" /> Matched: {role.base_snapshot.base_name}, {role.base_snapshot.state}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-slate-900 font-medium">Start Month</Label>
                <Input
                  data-testid={`start-month-input-${index}`}
                  type="number"
                  min="1"
                  max="12"
                  value={role.start_month}
                  onChange={(e) => updateRole(index, 'start_month', parseInt(e.target.value))}
                  className="h-12 mt-2"
                />
              </div>
              <div>
                <Label className="text-slate-900 font-medium">Start Year</Label>
                <Input
                  data-testid={`start-year-input-${index}`}
                  type="number"
                  min="1950"
                  max="2100"
                  value={role.start_year}
                  onChange={(e) => updateRole(index, 'start_year', parseInt(e.target.value))}
                  className="h-12 mt-2"
                />
              </div>
              <div>
                <Label className="text-slate-900 font-medium">End Month</Label>
                <Input
                  data-testid={`end-month-input-${index}`}
                  type="number"
                  min="1"
                  max="12"
                  value={role.end_month}
                  onChange={(e) => updateRole(index, 'end_month', parseInt(e.target.value))}
                  className="h-12 mt-2"
                />
              </div>
              <div>
                <Label className="text-slate-900 font-medium">End Year</Label>
                <Input
                  data-testid={`end-year-input-${index}`}
                  type="number"
                  min="1950"
                  max="2100"
                  value={role.end_year}
                  onChange={(e) => updateRole(index, 'end_year', parseInt(e.target.value))}
                  className="h-12 mt-2"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-900 font-medium">Occupational Specialty Code *</Label>
              <p className="text-xs text-slate-500 mt-0.5">Enlisted MOS, Officer AOC, Warrant Officer specialty, or Navy/Coast Guard Rating.</p>
              <div className="flex gap-2 mt-2">
                <Input
                  data-testid={`mos-code-input-${index}`}
                  value={role.mos_code}
                  onChange={(e) => { updateRole(index, 'mos_code', e.target.value.toUpperCase()); setMosLookupError(null); }}
                  className="h-12 flex-1"
                  placeholder="e.g. 11B (MOS), 11A (AOC), 153A (WO)"
                />
                <Button
                  data-testid={`lookup-mos-btn-${index}`}
                  onClick={() => lookupMOS(index)}
                  disabled={loadingMos}
                  className="bg-brand hover:bg-brand-hover text-white"
                >
                  <Search className="w-4 h-4 mr-2" />
                  {loadingMos && currentRole === index ? 'Looking up...' : 'Look Up Code'}
                </Button>
              </div>
            </div>

            {/* MOS Lookup Error — inline */}
            {mosLookupError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3" data-testid="mos-lookup-error">
                <p className="text-sm text-red-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {mosLookupError}
                </p>
              </div>
            )}

            {/* Branch change cleared MOS notice */}
            {branchClearedMsg && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="branch-cleared-msg">
                <p className="text-sm text-amber-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {branchClearedMsg}
                </p>
              </div>
            )}

            {/* MOS Title Display */}
            {role.mos_title && (
              <div className="bg-brand-light border border-brand/20 rounded-md p-3">
                <p className="text-sm text-brand font-medium flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  MOS: {role.mos_code} - {sanitizeMosTitle(role.mos_title, role.rank)}
                </p>
              </div>
            )}

            {role.mos_data && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  MOS data loaded. {role.selected_knowledge.length + role.selected_skills.length + role.selected_abilities.length} KSA items selected.
                </p>
              </div>
            )}

            {!role.mos_data && role.mos_code && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Click &quot;Look Up Code&quot; to fetch Knowledge, Skills &amp; Abilities for this specialty code.
                </p>
              </div>
            )}
          </div>
        </Card>
      ))}

      <Button
        data-testid="add-military-role-btn"
        onClick={addRole}
        variant="outline"
        className="w-full border-dashed border-2 border-slate-300 text-slate-700 hover:bg-slate-50"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add another military position
      </Button>

      {/* ===== Security Clearance — career-level, optional (compact, 2-col) ===== */}
      <Card className="p-4 sm:p-5 border border-slate-200" data-testid="security-clearance-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-slate-500" />
            <h3 className="text-base font-semibold text-slate-900">Security Clearance</h3>
          </div>
          <span className="text-xs text-slate-400 font-normal">Optional</span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">Applies to your career, not a single position.</p>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 mt-2.5">
          <div className="sm:col-span-7">
            <Label className="text-slate-700 font-medium text-xs">Clearance level</Label>
            <div className="relative mt-1">
              <select
                data-testid="career-clearance-select"
                value={clearanceUiValue}
                onChange={(e) => onClearanceChange(e.target.value)}
                className="appearance-none flex h-11 w-full rounded-md border border-slate-300 bg-white pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              >
                <option value="">None</option>
                <option value="confidential">Confidential</option>
                <option value="secret">Secret</option>
                <option value="top_secret">Top Secret</option>
                <option value="top_secret_sci">Top Secret with SCI</option>
                <option value="doe_l">DOE L</option>
                <option value="doe_q">DOE Q</option>
                <option value="other">Other / Not sure</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            </div>
          </div>
          {careerClearance && (
            <div className="sm:col-span-5">
              <Label className="text-slate-700 font-medium text-xs">Current access <span className="text-slate-400 font-normal">(optional)</span></Label>
              <div className="relative mt-1">
                <select
                  data-testid="clearance-access-select"
                  value={careerClearanceAccess}
                  onChange={(e) => { setCareerClearanceAccess(e.target.value); setIsDirty(true); }}
                  className="appearance-none flex h-11 w-full rounded-md border border-slate-300 bg-white pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="">Select…</option>
                  <option value="current_access">Currently using this clearance</option>
                  <option value="previously_held">Held previously, not currently using</option>
                  <option value="unknown">Not sure</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* ===== Military Qualifications — career-level, optional, compact ===== */}
      <Card className="p-4 sm:p-5 border border-slate-200" data-testid="military-qualifications-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-slate-500" />
            <h3 className="text-base font-semibold text-slate-900">Military Qualifications</h3>
          </div>
          <span className="text-xs text-slate-400 font-normal">Optional</span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">Add badges, tabs, schools and other military qualifications that may strengthen your translation.</p>

        {qualificationItems.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="qualifications-summary">
            {qualificationItems.map((sa) => {
              const info = qualCatalog[sa.award_id];
              const name = info?.display_name || sa.display_name || sa.award_id;
              const qty = sa.quantity || 1;
              return (
                <span
                  key={sa.award_id}
                  data-testid={`qualification-chip-${sa.award_id}`}
                  className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-slate-100 border border-slate-200 rounded-md text-xs font-medium text-slate-700"
                >
                  {name}{qty > 1 ? ` ×${qty}` : ''}
                  <button
                    data-testid={`qualification-remove-${sa.award_id}`}
                    onClick={() => removeQualification(sa.award_id)}
                    className="p-0.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {!qualOpen ? (
          <Button
            data-testid="add-qualification-details-btn"
            onClick={() => setQualOpen(true)}
            variant="outline"
            size="sm"
            className="mt-2.5 border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add qualifications
          </Button>
        ) : (
          <div className="mt-2.5 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="qualification-search-input"
                autoFocus
                placeholder="Search: Ranger, CIB, Airborne, Air Assault, SWO…"
                value={qualSearch}
                onChange={(e) => setQualSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            {qualResults.length > 0 && (
              <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-slate-100" data-testid="qualification-results">
                {qualResults.map((entry) => {
                  const already = structuredAwards.find(a => a.award_id === entry.award_id);
                  return (
                    <button
                      key={entry.award_id}
                      data-testid={`qualification-result-${entry.award_id}`}
                      onClick={() => addQualification(entry)}
                      disabled={!!already}
                      className={`w-full text-left px-3 py-2 transition-colors ${already ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'}`}
                    >
                      <span className="text-sm font-medium text-slate-900">{entry.display_name}</span>
                      {entry.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{entry.description}</p>}
                    </button>
                  );
                })}
              </div>
            )}
            <button
              onClick={() => { setQualOpen(false); setQualSearch(''); }}
              className="text-xs text-slate-500 hover:text-brand transition-colors"
              data-testid="qualification-done-btn"
            >
              Done
            </button>
          </div>
        )}
      </Card>

      {/* ===== Military Awards — same shared records as Step 5, optional, compact ===== */}
      <Card className="p-4 sm:p-5 border border-slate-200" data-testid="military-awards-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-slate-500" />
            <h3 className="text-base font-semibold text-slate-900">Military Awards</h3>
          </div>
          <span className="text-xs text-slate-400 font-normal">Optional</span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">Decorations &amp; medals. These sync with the Awards step.</p>

        {awardItems.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="military-awards-summary">
            {awardItems.map((sa) => {
              const info = qualCatalog[sa.award_id];
              const name = info?.display_name || sa.display_name || sa.award_id;
              const qty = sa.quantity || 1;
              return (
                <span
                  key={sa.award_id}
                  data-testid={`military-award-chip-${sa.award_id}`}
                  className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-slate-100 border border-slate-200 rounded-md text-xs font-medium text-slate-700"
                >
                  {name}{qty > 1 ? ` ×${qty}` : ''}
                  <button
                    data-testid={`military-award-remove-${sa.award_id}`}
                    onClick={() => removeQualification(sa.award_id)}
                    className="p-0.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {!awardsOpen ? (
          <Button
            data-testid="add-military-awards-btn"
            onClick={() => setAwardsOpen(true)}
            variant="outline"
            size="sm"
            className="mt-2.5 border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add / edit military awards
          </Button>
        ) : (
          <div className="mt-2.5 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="military-award-search-input"
                autoFocus
                placeholder="Search: ARCOM, Bronze Star, MSM, Purple Heart…"
                value={awardSearch}
                onChange={(e) => setAwardSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            {awardResults.length > 0 && (
              <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-slate-100" data-testid="military-award-results">
                {awardResults.map((entry) => {
                  const already = structuredAwards.find(a => a.award_id === entry.award_id);
                  return (
                    <button
                      key={entry.award_id}
                      data-testid={`military-award-result-${entry.award_id}`}
                      onClick={() => addQualification(entry)}
                      disabled={!!already}
                      className={`w-full text-left px-3 py-2 transition-colors ${already ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'}`}
                    >
                      <span className="text-sm font-medium text-slate-900">{entry.display_name}</span>
                      {entry.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{entry.description}</p>}
                    </button>
                  );
                })}
              </div>
            )}
            <button
              onClick={() => { setAwardsOpen(false); setAwardSearch(''); }}
              className="text-xs text-slate-500 hover:text-brand transition-colors"
              data-testid="military-award-done-btn"
            >
              Done
            </button>
          </div>
        )}
      </Card>

      {/* MOS KSA Selection Dialog */}
      <Dialog open={mosDialogOpen} onOpenChange={(o) => { setMosDialogOpen(o); if (!o) setExpandedKSA(null); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col max-sm:fixed max-sm:inset-0 max-sm:left-0 max-sm:top-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:max-w-none max-sm:w-screen max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:rounded-none max-sm:p-0 max-sm:gap-0" data-testid="mos-ksa-dialog">
          <DialogHeader className="flex-shrink-0 max-sm:px-4 max-sm:pt-4 max-sm:pb-3 max-sm:pr-10 max-sm:border-b max-sm:border-slate-200 max-sm:space-y-0">
            <DialogTitle className="text-2xl font-bold text-slate-900 max-sm:text-lg">
              {mosData?.title ? `${mosData.mos_code} - ${mosData.title}` : 'Select Capabilities'}
            </DialogTitle>
            <p className="text-sm text-slate-600 mt-1 max-sm:mt-0.5 max-sm:text-xs">Items ranked by occupational importance. Top capabilities are pre-selected.</p>
          </DialogHeader>

          {/* SINGLE scrollable main region (header above, action footer below). On mobile this is the
              ONLY vertical scroll — guidance, tabs content and info sections all scroll together here;
              inner scroll regions are disabled at narrow widths. Desktop keeps its existing inner
              scrolling (wrapper just delegates: overflow-hidden + flex-col + gap). */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden sm:gap-4 max-sm:overflow-y-auto max-sm:overflow-x-hidden max-sm:px-4 max-sm:py-3 max-sm:gap-3">
          {showKsaTip && (
            <div data-testid="ksa-tip-panel" className="mx-1 mb-1 rounded-lg bg-blue-50 border border-blue-200 border-l-4 border-l-blue-600 px-3 py-2.5">
              <p className="text-sm font-semibold text-blue-950">Understand each KSA</p>
              <p className="text-xs text-slate-700 leading-relaxed mt-0.5">Tap or click a row to see its description. Use the checkbox to select or deselect that KSA.</p>
              <div className="flex mt-2">
                <Button
                  data-testid="ksa-tip-got-it-btn"
                  size="sm"
                  className="h-7 text-xs bg-blue-600 text-white hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  onClick={() => { help.dismissKsaTip(); setShowKsaTip(false); }}
                >
                  Got it
                </Button>
              </div>
            </div>
          )}

          {mosData && (
            <Tabs defaultValue="ranked" className="w-full flex-1 flex flex-col overflow-hidden max-sm:flex-none max-sm:overflow-visible" onValueChange={() => setExpandedKSA(null)}>
              <TabsList className="flex w-full overflow-x-auto justify-start flex-shrink-0 md:grid md:grid-cols-4 max-sm:sticky max-sm:top-0 max-sm:z-10 max-sm:bg-white">
                <TabsTrigger value="ranked" data-testid="ranked-tab" className="shrink-0 text-xs md:text-sm">
                  Top Capabilities
                </TabsTrigger>
                <TabsTrigger value="knowledge" data-testid="knowledge-tab" className="shrink-0 text-xs md:text-sm">
                  Knowledge ({mosData.knowledge?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="skills" data-testid="skills-tab" className="shrink-0 text-xs md:text-sm">
                  Skills ({mosData.skills?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="abilities" data-testid="abilities-tab" className="shrink-0 text-xs md:text-sm">
                  Abilities ({mosData.abilities?.length || 0})
                </TabsTrigger>
              </TabsList>

              {/* Top Capabilities — unified ranked view */}
              <TabsContent value="ranked" className="mt-4 overflow-y-auto flex-1 max-sm:mt-3 max-sm:overflow-visible max-sm:flex-none" data-testid="ranked-list">
                {(mosData.ranked_items?.length || 0) > 0 ? (
                  <div className="space-y-0.5">
                    {mosData.ranked_items.filter(item => item.importance >= 40).map((item, idx) => {
                      const catMap = { knowledge: 'knowledge', skill: 'skills', ability: 'abilities' };
                      const catKey = catMap[item.category] || item.category;
                      const isChecked = selectedKSA[catKey]?.includes(item.name) || false;
                      const catLabel = item.category === 'knowledge' ? 'K' : item.category === 'skill' ? 'S' : 'A';
                      const catColor = item.category === 'knowledge'
                        ? 'bg-blue-100 text-blue-700'
                        : item.category === 'skill'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700';
                      return (
                        <div key={idx} className={`rounded-md transition-colors ${isChecked ? 'bg-brand-light' : ''}`}>
                          <div className="flex items-center gap-3 px-3 py-2">
                            <Checkbox
                              data-testid={`ranked-checkbox-${idx}`}
                              checked={isChecked}
                              onCheckedChange={() => toggleKSA(catKey, item.name)}
                              aria-label={`Select ${item.name}`}
                            />
                            <button
                              type="button"
                              data-testid={`ksa-row-${item.category}-${idx}`}
                              onClick={() => toggleExpand(`${item.category}:${item.name}`)}
                              aria-expanded={expandedKSA === `${item.category}:${item.name}`}
                              aria-controls={item.description ? `ksa-desc-ranked-${idx}` : undefined}
                              className="flex-1 min-w-0 flex items-center gap-3 text-left rounded-md hover:bg-slate-50 -mx-1 px-1 py-0.5"
                            >
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${catColor} flex-shrink-0 leading-none`}>
                                {catLabel}
                              </span>
                              <span className="text-sm text-slate-800 leading-tight flex-1 min-w-0">{item.name}</span>
                              <span className="flex items-center gap-1.5 flex-shrink-0" aria-hidden="true">
                                <span className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden block">
                                  <span className="h-full bg-brand rounded-full block" style={{ width: `${item.importance}%` }} />
                                </span>
                                <span className="text-[10px] text-slate-400 font-mono w-6 text-right">{item.importance}</span>
                              </span>
                            </button>
                          </div>
                          {expandedKSA === `${item.category}:${item.name}` && item.description && (
                            <p id={`ksa-desc-ranked-${idx}`} data-testid={`ksa-desc-ranked-${idx}`} className="px-3 pb-2 pl-11 text-xs text-slate-600 leading-relaxed break-words">
                              {item.description}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 p-4 text-center">No ranked data available</p>
                )}
              </TabsContent>

              {/* Individual category tabs — also sorted by importance */}
              {['knowledge', 'skills', 'abilities'].map(catKey => (
                <TabsContent key={catKey} value={catKey} className="mt-4 overflow-y-auto flex-1 max-sm:mt-3 max-sm:overflow-visible max-sm:flex-none" data-testid={`${catKey}-list`}>
                  {(() => {
                    const catFilter = catKey === 'knowledge' ? 'knowledge' : catKey === 'skills' ? 'skill' : 'ability';
                    const items = (mosData.ranked_items || []).filter(i => i.category === catFilter);
                    const fallbackItems = (mosData[catKey] || []).map(name => ({ name, importance: 0, category: catFilter }));
                    const displayItems = items.length > 0 ? items : fallbackItems;
                    return displayItems.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                        {displayItems.map((item, idx) => {
                          const nm = typeof item === 'string' ? item : item.name;
                          const desc = typeof item === 'string' ? '' : item.description;
                          const rowKey = `${catFilter}:${nm}`;
                          const isExpanded = expandedKSA === rowKey;
                          const descId = `ksa-desc-${catKey}-${idx}`;
                          return (
                            <div key={idx} className="rounded-md">
                              <div className="flex items-start gap-3 p-2 hover:bg-slate-50 rounded-md">
                                <Checkbox
                                  data-testid={`${catKey.slice(0, -1 * (catKey === 'abilities' ? 4 : 1))}-checkbox-${idx}`}
                                  className="mt-0.5"
                                  checked={selectedKSA[catKey]?.includes(nm) || false}
                                  onCheckedChange={() => toggleKSA(catKey, nm)}
                                  aria-label={`Select ${nm}`}
                                />
                                <button
                                  type="button"
                                  data-testid={`ksa-row-${catKey}-${idx}`}
                                  onClick={() => toggleExpand(rowKey)}
                                  aria-expanded={isExpanded}
                                  aria-controls={desc ? descId : undefined}
                                  className="flex-1 min-w-0 flex items-center gap-2 text-left"
                                >
                                  <span className="text-sm text-slate-700 leading-tight flex-1 min-w-0">{nm}</span>
                                  {item.importance > 0 && (
                                    <span className="text-[10px] text-slate-400 font-mono flex-shrink-0" aria-hidden="true">{item.importance}</span>
                                  )}
                                </button>
                              </div>
                              {isExpanded && desc && (
                                <p id={descId} data-testid={descId} className="px-2 pb-2 pl-9 text-xs text-slate-600 leading-relaxed break-words">
                                  {desc}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 p-4 text-center">No {catKey} data available</p>
                    );
                  })()}
                </TabsContent>
              ))}
            </Tabs>
          )}

          {/* Work Activities & Tools (read-only informational sections) */}
          {mosData && (mosData.work_activities?.length > 0 || mosData.tools_technology?.length > 0) && (
            <div className="border-t border-slate-200 pt-3 mt-2 space-y-3 flex-shrink-0 max-h-[140px] overflow-y-auto max-sm:max-h-none max-sm:overflow-visible">
              {mosData.work_activities?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Key Work Activities</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {mosData.work_activities.slice(0, 6).map((wa, idx) => (
                      <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">{wa.name}</span>
                    ))}
                  </div>
                </div>
              )}
              {mosData.tools_technology?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Tools & Systems</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {mosData.tools_technology.slice(0, 5).map((tt, idx) => (
                      <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">{tt.category}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          </div>{/* end single scroll region */}

          <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-200 flex-shrink-0 max-sm:mt-0 max-sm:flex-col max-sm:items-stretch max-sm:gap-3 max-sm:px-4 max-sm:py-3 max-sm:pb-[calc(0.75rem+env(safe-area-inset-bottom))] max-sm:bg-white">
            <p className="text-sm text-slate-600 max-sm:text-center">
              Selected: <span className="font-medium">{selectedKSA.knowledge.length + selectedKSA.skills.length + selectedKSA.abilities.length}</span> items
              <span className="text-slate-400 ml-2">
                (K: {selectedKSA.knowledge.length}, S: {selectedKSA.skills.length}, A: {selectedKSA.abilities.length})
              </span>
            </p>
            <div className="flex gap-3 max-sm:w-full">
              <Button
                data-testid="cancel-ksa-btn"
                onClick={() => setMosDialogOpen(false)}
                variant="outline"
                className="border-slate-300 text-slate-700 max-sm:flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-ksa-btn"
                onClick={saveKSASelection}
                className="bg-brand hover:bg-brand-hover text-white max-sm:flex-1"
              >
                Save Selection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MilitaryStep;
